import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { updateRecordImage } from "../../utils/api";
import CustomModal from "../Modal";
import { Button, CircularProgress } from "@mui/material";

const ImageAttachmentModal = ({
  open,
  setOpen,
  record,
  imageField = "image",
  title = "Image",
  currentImageUrl,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useWebcam, setUseWebcam] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, JPEG, WEBP)");
      return;
    }

    // Max 15MB limit
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image size exceeds 15MB. Please choose a smaller image.");
      return;
    }

    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    stopWebcam();
  };

  // Start live webcam stream
  const startWebcam = async () => {
    try {
      setUseWebcam(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      setWebcamStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.warn("Webcam access error:", err);
      setUseWebcam(false);
      // Fallback to standard camera input
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    }
  };

  // Stop live webcam stream
  const stopWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach((track) => track.stop());
      setWebcamStream(null);
    }
    setUseWebcam(false);
  };

  // Capture frame from live webcam
  const captureWebcamSnapshot = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setSelectedFile(file);
          setPreviewUrl(URL.createObjectURL(blob));
          stopWebcam();
        }
      },
      "image/jpeg",
      0.9
    );
  };

  const handleClose = () => {
    stopWebcam();
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setOpen(false);
  };

  const handleUpdate = async () => {
    if (!selectedFile) {
      toast.error("Please take a photo or select an image from gallery first!");
      return;
    }

    const recordId = record?._id || record?.id;
    if (!recordId) {
      toast.error("Record ID not found");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("id", recordId);
      formData.append("field", imageField);
      formData.append("type", record?.type || (record?.paymentType ? "payment" : "order"));

      const [success, resData] = await updateRecordImage(formData);
      if (success) {
        toast.success(`✅ ${title} updated successfully!`);
        handleClose();
        onSuccess?.();
      } else {
        toast.error(resData || `Failed to update ${title}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error uploading image");
    } finally {
      setLoading(false);
    }
  };

  const customerName =
    record?.customer?.userName || record?.customer?.name || "Customer";
  const productName =
    record?.product?.productName || record?.description || "";

  return (
    <CustomModal
      open={open}
      setOpen={handleClose}
      title={`Update / Replace ${title}`}
      button={
        <div className="flex items-center justify-end gap-2 w-full pt-2">
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={loading || !selectedFile}
            style={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: !selectedFile ? "#94a3b8" : "#2563eb",
            }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <CircularProgress size={16} color="inherit" />
                <span>Uploading...</span>
              </span>
            ) : (
              <span>🚀 Update Image</span>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3.5 text-xs text-slate-700">
        {/* Record Summary Card */}
        <div className="bg-slate-50 border border-slate-200 p-2.5 rounded-lg flex flex-col gap-1">
          <div className="flex justify-between items-center flex-wrap gap-1">
            <span className="font-bold text-slate-900">
              👤 {customerName}
            </span>
            {record?.quantity && (
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-mono font-bold text-[11px]">
                Qty: {record?.quantity}
              </span>
            )}
          </div>
          {productName && (
            <div className="text-slate-600 font-medium text-[11px]">
              📦 {productName}
            </div>
          )}
        </div>

        {/* Existing Image Thumbnail (if any) */}
        {currentImageUrl && !previewUrl && (
          <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <img
                src={currentImageUrl}
                alt="Current"
                className="w-12 h-12 object-cover rounded-md border border-amber-300 shadow-2xs"
              />
              <div>
                <div className="font-bold text-slate-800">Current Image Attached</div>
                <div className="text-[11px] text-slate-500">Click below to replace it</div>
              </div>
            </div>
            <a
              href={currentImageUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-semibold text-xs whitespace-nowrap"
            >
              View Full
            </a>
          </div>
        )}

        {/* Hidden inputs for Camera and Gallery */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Attachment Options Buttons */}
        {!useWebcam && (
          <div className="grid grid-cols-2 gap-2.5">
            {/* Camera Button */}
            <button
              type="button"
              onClick={() => {
                if (
                  navigator.mediaDevices &&
                  navigator.mediaDevices.getUserMedia &&
                  window.innerWidth > 768
                ) {
                  startWebcam();
                } else if (cameraInputRef.current) {
                  cameraInputRef.current.click();
                }
              }}
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 border-dashed border-indigo-300 hover:border-indigo-600 bg-indigo-50/50 hover:bg-indigo-100/70 text-indigo-900 transition active:scale-98 cursor-pointer group"
            >
              <span className="text-2xl group-hover:scale-110 transition-transform">📷</span>
              <span className="font-bold text-xs">Camera</span>
              <span className="text-[10px] text-indigo-600">Take Live Photo</span>
            </button>

            {/* Gallery Button */}
            <button
              type="button"
              onClick={() => {
                if (galleryInputRef.current) {
                  galleryInputRef.current.click();
                }
              }}
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-600 bg-emerald-50/50 hover:bg-emerald-100/70 text-emerald-900 transition active:scale-98 cursor-pointer group"
            >
              <span className="text-2xl group-hover:scale-110 transition-transform">🖼️</span>
              <span className="font-bold text-xs">Gallery / File</span>
              <span className="text-[10px] text-emerald-600">Choose from Device</span>
            </button>
          </div>
        )}

        {/* Live Webcam Stream View */}
        {useWebcam && (
          <div className="flex flex-col items-center gap-2 bg-slate-900 p-3 rounded-xl border border-slate-700">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full max-h-56 rounded-lg bg-black object-contain"
            />
            <div className="flex items-center gap-2 w-full justify-center">
              <button
                type="button"
                onClick={captureWebcamSnapshot}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-1.5 rounded-lg text-xs flex items-center gap-1 shadow transition active:scale-95 cursor-pointer"
              >
                <span>📸</span>
                <span>Snap Photo</span>
              </button>
              <button
                type="button"
                onClick={stopWebcam}
                className="bg-slate-700 hover:bg-slate-600 text-white font-semibold px-3 py-1.5 rounded-lg text-xs transition active:scale-95 cursor-pointer"
              >
                Cancel Camera
              </button>
            </div>
          </div>
        )}

        {/* New Image Preview Box */}
        {previewUrl && (
          <div className="flex flex-col gap-2 p-2.5 rounded-xl border-2 border-blue-400 bg-blue-50/40">
            <div className="flex items-center justify-between">
              <span className="font-bold text-blue-900 flex items-center gap-1">
                <span>✨</span>
                <span>New Replacement Preview</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }}
                className="text-red-600 hover:text-red-800 font-bold text-xs cursor-pointer hover:underline"
              >
                ✕ Remove
              </button>
            </div>
            <div className="relative rounded-lg overflow-hidden border border-blue-200 bg-white flex justify-center items-center max-h-60">
              <img
                src={previewUrl}
                alt="New Preview"
                className="max-h-60 w-auto object-contain rounded"
              />
            </div>
            {selectedFile && (
              <div className="text-[11px] text-slate-500 flex justify-between font-mono">
                <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                <span>{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default ImageAttachmentModal;
